import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, ArrowRight, AlertCircle, LogIn, UserPlus, Mail, Lock, Eye, EyeOff, Loader2, Check, Sparkles
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_nutrifit-portal/artifacts/s4srivcq_Logo%20Icono%20Solo%20Nutriquimia%20SVG.svg";

const FORM_INFO = {};

const SharedFormPage = () => {
  const { token, formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [distributorName, setDistributorName] = useState('');
  const [phase, setPhase] = useState(location.state?.fromRegister ? 'registered' : 'intro');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const formInfo = FORM_INFO[formId];

  const validateToken = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/registration-info/${token}`);
      setDistributorName(res.data.distributor_name);
      setValid(true);
    } catch {
      setValid(false);
    } finally {
      setValidating(false);
    }
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authLogin(email, password);
      toast.success('Sesión iniciada correctamente');
      const redirectPath = formInfo?.clientPath || '/client';
      navigate(redirectPath, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  // --- LOADING ---
  if (validating) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  // --- INVALID ---
  if (!valid || !formInfo) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Link Inválido
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Este link no es válido. Contacta a tu distribuidor para obtener uno nuevo.
          </p>
          <Button onClick={() => navigate('/')}
            className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-full px-8 font-bold shadow-lg shadow-emerald-500/25">
            Ir al Inicio
          </Button>
        </div>
      </div>
    );
  }

  const FormIcon = formInfo.icon;

  // --- INTRO ---
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="shared-form-intro">
        <div className="fixed top-0 right-0 w-80 h-80 bg-red-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="fixed bottom-0 left-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />

        <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
          <div className="max-w-md w-full">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Nutriquimia" className="w-14 h-14 object-contain" />
              <span className="font-bold text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#467322' }}>
                Nutriquimia App
              </span>
            </div>

            {/* Card */}
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
              <div className="h-2" style={{ background: `linear-gradient(90deg, ${formInfo.color}, ${formInfo.color}cc, ${formInfo.color})` }} />

              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl flex items-center justify-center mx-auto mb-5 border border-red-100/50">
                  <FormIcon size={36} style={{ color: formInfo.color }} />
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {formInfo.name}
                </h1>
                <p className="text-sm text-gray-500 mb-1">Has sido invitado por</p>
                <p className="text-lg font-bold mb-5" style={{ color: '#467322' }}>{distributorName}</p>

                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Tu distribuidor te ha invitado a completar un formulario de salud para poder evaluar tu bienestar y ofrecerte recomendaciones personalizadas.
                </p>

                {/* Form Details */}
                <div className="bg-gray-50/80 rounded-2xl p-5 mb-6 text-left border border-gray-100/50">
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Sparkles size={16} style={{ color: formInfo.color }} />
                    ¿Qué evaluaremos?
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {formInfo.description}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                      {formInfo.sections} secciones
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                      ~10 minutos
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-5">
                  Al presionar <span className="font-semibold" style={{ color: formInfo.color }}>Iniciar</span>, podrás acceder con tu cuenta o crear una nueva para completar el formulario.
                </p>

                <button
                  onClick={() => setPhase('auth')}
                  className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-full text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  style={{ background: `linear-gradient(135deg, ${formInfo.color}, ${formInfo.color}dd)`, boxShadow: `0 10px 25px -5px ${formInfo.color}40` }}
                  data-testid="start-form-btn"
                >
                  Iniciar
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- REGISTERED (after registration, invite to login) ---
  if (phase === 'registered') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="shared-form-registered">
        <div className="fixed top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />

        <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
          <div className="max-w-md w-full">
            <div className="flex items-center justify-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Nutriquimia" className="w-14 h-14 object-contain" />
              <span className="font-bold text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#467322' }}>
                Nutriquimia App
              </span>
            </div>

            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
              <div className="h-2" style={{ background: 'linear-gradient(90deg, #3A601C, #467322, #5A8A2D)' }} />
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-50 to-green-50 rounded-full flex items-center justify-center mx-auto mb-5 border-4 border-emerald-100/50">
                  <Check size={36} style={{ color: '#467322' }} strokeWidth={3} />
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Registro Exitoso
                </h1>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  Tu cuenta ha sido creada correctamente. Ahora inicia sesión con tus credenciales para completar el formulario <span className="font-semibold" style={{ color: formInfo.color }}>{formInfo.name}</span>.
                </p>

                <button
                  onClick={() => setPhase('auth')}
                  className="w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-full text-base font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #3A601C, #467322)', boxShadow: '0 10px 25px -5px rgba(58, 96, 28, 0.3)' }}
                  data-testid="go-login-after-register"
                >
                  <LogIn size={18} />
                  Iniciar Sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- AUTH (Login / Register) ---
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="shared-form-auth">
      <div className="fixed top-0 right-0 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-56 h-56 bg-red-500/5 rounded-full blur-3xl -translate-x-1/2 pointer-events-none" />

      <div className="flex-1 flex items-center justify-center px-4 py-8 relative">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Nutriquimia" className="w-12 h-12 object-contain" />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#467322' }}>
              Nutriquimia App
            </span>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50 overflow-hidden">
            <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #3A601C, #467322, #5A8A2D)' }} />
            <div className="p-7">
              {/* Form context */}
              <div className="flex items-center gap-3 p-3 bg-red-50/60 rounded-xl border border-red-100/40 mb-5">
                <FormIcon size={20} style={{ color: formInfo.color }} />
                <div>
                  <p className="text-xs font-bold text-gray-800">{formInfo.name}</p>
                  <p className="text-[10px] text-gray-500">Inicia sesión para completar el formulario</p>
                </div>
              </div>

              <h2 className="text-xl font-bold text-gray-900 text-center mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Iniciar Sesión
              </h2>
              <p className="text-xs text-gray-400 text-center mb-5">
                Ingresa tus credenciales para continuar
              </p>

              <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Correo Electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="correo@ejemplo.com"
                      autoComplete="off"
                      className="pl-10 bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 rounded-xl h-11"
                      data-testid="form-login-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Tu contraseña"
                      autoComplete="new-password"
                      className="pl-10 pr-10 bg-gray-50 border-transparent focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 rounded-xl h-11"
                      data-testid="form-login-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 text-white py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #3A601C, #467322)', boxShadow: '0 10px 25px -5px rgba(58, 96, 28, 0.3)' }}
                  data-testid="form-login-submit"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                  Iniciar Sesión
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">¿No tienes cuenta?</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Register CTA */}
              <div className="bg-gradient-to-br from-emerald-50/80 to-green-50/80 rounded-2xl p-5 border border-emerald-100/30 text-center">
                <UserPlus size={24} className="mx-auto mb-2" style={{ color: '#467322' }} />
                <p className="text-sm font-bold text-gray-800 mb-1">Crea tu cuenta gratis</p>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  Regístrate como cliente de <span className="font-semibold" style={{ color: '#467322' }}>{distributorName}</span> para completar este formulario y acceder a todas las herramientas de Nutriquimia.
                </p>
                <Link
                  to={`/register/${token}`}
                  state={{ returnTo: `/health-form/${token}/${formId}`, showRegistered: true }}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold border-2 transition-all duration-300 hover:-translate-y-0.5"
                  style={{ borderColor: '#467322', color: '#467322' }}
                  data-testid="form-register-link"
                >
                  <UserPlus size={16} />
                  Registrarme
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-5">
            Distribuidor: <span className="font-medium">{distributorName}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedFormPage;
